import { useCallback, useEffect, useRef, useState } from 'react';
import attachmentsService from '../../api/services/attachments.service';
import { attachKind, attachReject } from '../../utils/attachments';

/* The draft's files (spec/32). Each is uploaded the moment it is picked, so a
   20 MB video is on the server by the time Send is pressed and the record can
   carry a reference; the File stays here too, because the email leaves the
   browser through Gmail with the bytes attached. A failed upload is a row with
   Retry, never a silent drop. A refused file is reported, never dropped either.

   Each attachment: { id, file, name, size, kind, label, contentType, thumb,
   state: 'uploading' | 'ready' | 'failed', pct, url, key } */
export default function useEmailAttachments(networkId) {
  const [attachments, setAttachments] = useState([]);
  const [rejects, setRejects] = useState([]);
  /* the list as of the last add/remove, for checks that can't wait for a render */
  const listRef = useRef([]);
  const seq = useRef(1);
  const controllers = useRef({});

  useEffect(() => {
    listRef.current = attachments;
  }, [attachments]);

  const patch = useCallback((id, fields) => {
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...fields } : a)));
  }, []);

  const upload = useCallback(
    async (a) => {
      patch(a.id, { state: 'uploading', pct: 0 });
      const ctrl = new AbortController();
      controllers.current[a.id] = ctrl;
      try {
        const r = await attachmentsService.upload({
          networkId,
          file: a.file,
          signal: ctrl.signal,
          onProgress: (pct) => patch(a.id, { pct }),
        });
        patch(a.id, { state: 'ready', pct: 100, url: r.url, key: r.key, contentType: r.contentType || a.contentType });
      } catch {
        if (!ctrl.signal.aborted) patch(a.id, { state: 'failed', pct: 0 });
      } finally {
        delete controllers.current[a.id];
      }
    },
    [networkId, patch]
  );

  /* stop its upload and free its thumbnail */
  const drop = useCallback((a) => {
    controllers.current[a.id]?.abort();
    delete controllers.current[a.id];
    if (a.thumb) URL.revokeObjectURL(a.thumb);
  }, []);

  const add = useCallback(
    (files) => {
      const cur = [...listRef.current];
      const bad = [];
      const added = [];
      Array.from(files || []).forEach((file) => {
        const why = attachReject(file, cur);
        if (why) {
          bad.push({ name: file.name, why });
          return;
        }
        const k = attachKind(file.name);
        const a = {
          id: seq.current++,
          file,
          name: file.name,
          size: file.size,
          kind: k.kind,
          label: k.label,
          contentType: file.type || '',
          thumb: k.kind === 'image' ? URL.createObjectURL(file) : '',
          state: 'uploading',
          pct: 0,
          url: '',
          key: '',
        };
        cur.push(a);
        added.push(a);
      });
      listRef.current = cur;
      setAttachments(cur);
      setRejects(bad);
      added.forEach(upload);
      return added;
    },
    [upload]
  );

  /* returns the row's index, so the caller can keep keyboard focus nearby */
  const remove = useCallback(
    (id) => {
      const cur = listRef.current;
      const i = cur.findIndex((a) => a.id === id);
      if (i < 0) return -1;
      drop(cur[i]);
      const next = cur.filter((a) => a.id !== id);
      listRef.current = next;
      setAttachments(next);
      return i;
    },
    [drop]
  );

  const retry = useCallback(
    (id) => {
      const a = listRef.current.find((x) => x.id === id);
      if (a) upload(a);
    },
    [upload]
  );

  const clear = useCallback(() => {
    listRef.current.forEach(drop);
    listRef.current = [];
    setAttachments([]);
    setRejects([]);
  }, [drop]);

  const dismissRejects = useCallback(() => setRejects([]), []);

  /* leaving the page: stop the uploads and free the thumbnails */
  useEffect(() => () => listRef.current.forEach(drop), [drop]);

  return { attachments, rejects, add, remove, retry, clear, dismissRejects };
}
